/* global module*/

import React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import Slider from '../../common/Slider/index.tsx';
import NavButtons from '../../common/NavButtons.tsx';
import { withT, wrap } from '../../../lib/wrappers';
import { userApiValue, defaultApiValue } from '../../../lib/legacyHelpers';
import selectors, { bindSelectors, pick } from '../../../redux/selectors';
import a, { bindActionCreators } from '../../../redux/actions';
import numericInput from '../../../lib/numericInput';
import ResizableComponent from '../../../lib/base_classes/ResizableComponent';
import { getSliderWidth } from '../../layout/RouteContainer';
import { CustomizableButton } from '../../common/Customizable';
import Help from '../home/Help';

// We are ignoring goods_other_total - if advanced selected,
// user must answer advanced other good questions.
const GOODS_QUESTIONS = [
  'furnitureappliances', 'clothing',
  'other_entertainment', 'other_office', 'other_personalcare',
  'other_autoparts', 'other_medical',
];
const SERVICES_QUESTIONS = [
  'healthcare', 'education', 'communications',
  'vehicleservices', 'finance', 'household', 'charity', 'miscservices',
];

class ShoppingComponent extends ResizableComponent {

  constructor(props, context) {
    super(props, context);
    const shopping = this;

    shopping.state = {
      simple: true,
    };
    shopping.initResizeListener();
  }

  get t() {
    return this.props.t;
  }

  get _t() {
    return this.props._t;
  }

  get api_key_base() {
    return 'input_footprint_shopping';
  }

  get relevant_api_keys() {
    return GOODS_QUESTIONS.concat(SERVICES_QUESTIONS);
  }

  get goods_questions() {
    return GOODS_QUESTIONS;
  }

  get average_goods_expend() {
    const { average_footprint } = this.props;
    return defaultApiValue('input_footprint_shopping_goods_total', average_footprint);
  }

  get services_questions() {
    return SERVICES_QUESTIONS;
  }

  get average_services_expend() {
    const { average_footprint } = this.props;
    return defaultApiValue('input_footprint_shopping_services_total', average_footprint);
  }

  getAdvancedLabel(type) {
    const advancedLabels = {
      furnitureappliances: 'Furniture & appliances',
      clothing: 'Clothing',
      other_entertainment: 'Entertainment',
      other_office: 'Paper, office, & reading',
      other_personalcare: 'Personal care & cleaning',
      other_autoparts: 'Auto parts',
      other_medical: 'Medical',
      healthcare: 'Health Care',
      education: 'Information & Communication',
      communications: 'Medical',
      vehicleservices: 'Vehicle service',
      finance: 'Personal business & finance',
      household: 'Household maintenance & repair',
      charity: 'Organizations & Charity',
      miscservices: 'Other services',
    };
    return this.t(advancedLabels[type]);
  }

  apiKey(type) {
    if (GOODS_QUESTIONS.indexOf(type) >= 0) {
      return `input_footprint_shopping_goods_${type}`;
    }
    return `input_footprint_shopping_services_${type}`;
  }

  /*
   * Callbacks
   */

  updateMonthlyExpenditure(event) {
    const api_key = event.target.dataset.api_key;
    this.props.computeUserFootprint({ [api_key]: event.target.value });
  }

  /*
   * Simple/Advanced
   */

  get simple() {
    return this.state.simple;
  }

  get advanced() {
    return !this.state.simple;
  }

  setSimple() {
    const shopping = this;
    if (shopping.simple) return;
    shopping.setState({
      simple: true,
    });
    this.props.computeUserFootprint({
      input_footprint_shopping_goods_type: 0,
      input_footprint_shopping_goods_other_type: 1,
      input_footprint_shopping_services_type: 0,
    });
  }

  setAdvanced() {
    const shopping = this;
    if (shopping.advanced) return;
    shopping.setState({
      simple: false,
    });
    this.props.computeUserFootprint({
      input_footprint_shopping_goods_type: 1,
      input_footprint_shopping_goods_other_type: 1,
      input_footprint_shopping_services_type: 1,
    });
  }

  /*
   * Sliders
   */

  expendAverage(type) {
    const shopping = this;
    const api_key = shopping.apiKey(type);
    const { average_footprint } = this.props;
    return defaultApiValue(api_key, average_footprint);
  }

  get display_services_monthly_spending() {
    const amount = numericInput.format(
      Math.round(
        userApiValue(
          'input_footprint_shopping_services_total', this.props.user_footprint, this.props.average_footprint,
        ),
      ), { autoDecimal: true });

    // eslint-disable-next-line no-template-curly-in-string
    return this.t('${{amount}}', { amount });
  }

  updateServiceDistribution(multiplier) {
    const update_params = SERVICES_QUESTIONS.reduce((hash, serviceType) => {
      const apiKey = this.apiKey(serviceType);
      const newValue = multiplier * this.expendAverage(serviceType);
      return {
        ...hash,
        [apiKey]: newValue,
      };
    }, {});
    const total_services = multiplier * this.average_services_expend;

    update_params.input_footprint_shopping_services_total = total_services;
    this.props.computeUserFootprint(update_params);
  }

  get display_goods_monthly_spending() {
    const amount = numericInput.format(
      Math.round(
        userApiValue(
          'input_footprint_shopping_goods_total', this.props.user_footprint, this.props.average_footprint,
        ),
      ),
      { autoDecimal: true },
    );
    // eslint-disable-next-line no-template-curly-in-string
    return this.t('${{amount}}', { amount });
  }

  updateGoodsDistribution(multiplier) {
    const updateParams = GOODS_QUESTIONS.reduce((previous, goods_type) => {
      const apiKey = this.apiKey(goods_type);
      const newValue = multiplier * this.expendAverage(goods_type);
      return {
        ...previous,
        [apiKey]: newValue,
      };
    }, {});

    const totalGoods = multiplier * this.average_goods_expend;
    updateParams.input_footprint_shopping_goods_total = totalGoods;
    this.props.computeUserFootprint(updateParams);
  }

  resize() {
    const shopping = this;
    shopping.goods_slider.redraw({
      outer_width: getSliderWidth(),
    });
    shopping.services_slider.redraw({
      outer_width: getSliderWidth(),
    });
  }

  displayUserApiStateValue(api_suffix) {
    const apiKey = this.apiKey(api_suffix);
    return Math.round(
      userApiValue(
        apiKey, this.props.user_footprint, this.props.average_footprint,
      ),
    );
  }

  render() {
    return (
      <div className="cc-component" id="shopping">
        <header className="cc-component__header">
          <span className="cc-component__byline">{this.t('How much do you spend on each of the following?')}</span>
          <div className="cc-component__simple-advanced-toggle">
            <div className="btn-group" role="group" aria-label={this.t('Simple or advanced shopping questions')}>
              <CustomizableButton
                ui={this.props.ui}
                type="button"
                className={classnames('btn', { 'btn-default': !this.state.simple, 'btn-success': this.state.simple })}
                onClick={() => this.setSimple()}
              >{this._t('Simple').startCase()}</CustomizableButton>
              <CustomizableButton
                ui={this.props.ui}
                type="button"
                className={classnames('btn', { 'btn-default': this.state.simple, 'btn-success': !this.state.simple })}
                onClick={() => this.setAdvanced()}
              >{this.t('Advanced')}</CustomizableButton>
            </div>
          </div>
        </header>

        <div className="cc-component__form">
          {this.simple &&
            <div>
              <div className="panel panel-default">
                <div className="panel-heading">
                  {this.t('Goods')}
                  <Help
                    helpKey="goods"
                    t={s => this.t(s)}
                  />
                </div>
                <div className="panel-body">
                  <div className="label-container">
                    <span className="label label-slider">
                      {this.display_goods_monthly_spending} / {this.t('month')}
                    </span>
                  </div>
                  <Slider
                    id="shopping_goods_slider"
                    min={0.0001}
                    max={3}
                    step={0.01}
                    ticks={4}
                    labels={['0', this.props._t('average').startCase(), '2x', '3x']}
                    value={
                      userApiValue(
                        'input_footprint_shopping_goods_total', this.props.user_footprint, this.props.average_footprint,
                      ) / this.average_goods_expend
                    }
                    onChange={multiplier => this.updateGoodsDistribution(multiplier)}
                  />
                </div>
              </div>
              <div className="panel panel-default">
                <div className="panel-heading">{this.t('Services')}
                  <Help
                    helpKey="goods"
                    t={s => this.t(s)}
                  />
                </div>
                <div className="panel-body">
                  <div className="label-container">
                    <span className="label label-slider">
                      {this.display_services_monthly_spending} / {this.t('month')}
                    </span>
                  </div>
                  <Slider
                    id="shopping_services_slider"
                    min={0.0001}
                    max={3}
                    step={0.01}
                    ticks={4}
                    labels={['0', this.props._t('average').startCase(), '2x', '3x']}
                    value={
                      userApiValue(
                        'input_footprint_shopping_services_total',
                        this.props.user_footprint, this.props.average_footprint,
                      ) / this.average_services_expend
                    }
                    onChange={multiplier => this.updateServiceDistribution(multiplier)}
                  />
                </div>
              </div>
            </div>
          }
          {!this.simple &&
            <div>
              <div className="panel panel-default">
                <div className="panel-heading">{this.t('Goods')}</div>
                <div className="panel-body">
                  {this.goods_questions.map(goods_type => (
                    <div
                      key={`shopping_question_${goods_type}`}
                      className="cc-component__question"
                    >
                      <h4 className="cc-component__question-label">
                        {this.getAdvancedLabel(goods_type)}
                      </h4>
                      <div className="input-group">
                        <input
                          aria-label={this.getAdvancedLabel(goods_type)}
                          id={`input_${this.apiKey(goods_type)}`}
                          onChange={e => this.updateMonthlyExpenditure(e)}
                          value={this.displayUserApiStateValue(goods_type)}
                          placeholder={this.t('$/month', { ns: 'units' })}
                          data-type={goods_type}
                          data-api_key={this.apiKey(goods_type)}
                          className="form-control"
                        />
                        <span className="input-group-addon">{this.t('$/month', { ns: 'units' })}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="panel panel-default">
                <div className="panel-heading">{this.t('Services')}</div>
                <div className="panel-body">
                  {this.services_questions.map(service_type => (
                    <div
                      key={`shopping_question_${service_type}`}
                      className="cc-component__question"
                    >
                      <h4 className="cc-component__question-label">
                        {this.getAdvancedLabel(service_type)}
                      </h4>
                      <div className="input-group">
                        <input
                          aria-label={this.getAdvancedLabel(service_type)}
                          onChange={e => this.updateMonthlyExpenditure(e)}
                          value={this.displayUserApiStateValue(service_type)}
                          placeholder={this.t('$/month', { ns: 'units' })}
                          data-type={service_type}
                          data-api_key={this.apiKey(service_type)}
                          className="form-control"
                        />
                        <span className="input-group-addon">{this.t('$/month', { ns: 'units' })}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          }
          <div className="panel panel-default">
            <div className="panel-body">
              <p>
                <span
                  dangerouslySetInnerHTML={{
                    __html: this.t('Greenhouse gas emission factors from <i>the Comprehensive Environmental Data Archive for Economic and Environmental Systems Analysis</i>'),
                  }}
                />
                &nbsp;(<a
                  rel="noopener noreferrer"
                  target="_blank"
                  href="http://www.vitalmetricsgroup.com/data-ceda"
                ><i>{this.t('CEDA 3.0 Climate')}</i></a>).
              </p>
            </div>
          </div>
        </div>
        <NavButtons ui={this.props.ui} />
      </div>
    );
  }

}

const mapStateToProps = (state, ownProps) => bindSelectors(
  pick(selectors, [
    'user_footprint', 'average_footprint', 'ui',
  ]), state, ownProps,
);

const mapDispatchToProps = dispatch => bindActionCreators(
  pick(a, [
    'computeUserFootprint',
  ]),
  dispatch,
);

export default wrap(
  withT('shopping'),
  connect(mapStateToProps, mapDispatchToProps),
  ShoppingComponent,
);
